import React, { FC, useState, useRef, useEffect } from 'react';
import { useTranslation } from "react-i18next";

interface AudioRecorderProps {
    questionKey: string;
    onAudioRecorded: (questionKey: string, audioBlob: Blob) => void;
    onUploadAudio: (questionKey: string) => void;
}

const AudioRecorder: FC<AudioRecorderProps> = ({ questionKey, onAudioRecorded, onUploadAudio }) => {
    const [isRecording, setIsRecording] = useState(false);
    const [audioURL, setAudioURL] = useState('');
    const [recordingTime, setRecordingTime] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const { t } = useTranslation("guydeez");

    useEffect(() => {
        if (isRecording) {
            timerRef.current = setInterval(() => {
                setRecordingTime((prevTime) => prevTime + 1);
            }, 1000);
        } else if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [isRecording]);

    const startRecording = async () => {
        setRecordingTime(0);
        setError(null);
        // getUserMedia only exists in a secure context (HTTPS or localhost)
        // and on supported browsers — guard it so a missing API surfaces a
        // clear message instead of silently throwing.
        if (
            typeof navigator === 'undefined' ||
            !navigator.mediaDevices ||
            !navigator.mediaDevices.getUserMedia ||
            typeof MediaRecorder === 'undefined'
        ) {
            setError(
                t('recruitment_audio_unsupported', {
                    defaultValue:
                        "L'enregistrement audio n'est pas disponible (navigateur non compatible ou page non sécurisée).",
                }) as string
            );
            return;
        }
        try {
            if (audioURL) {
                URL.revokeObjectURL(audioURL);
            }
            audioChunksRef.current = [];
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorderRef.current = new MediaRecorder(stream);
            mediaRecorderRef.current.ondataavailable = (event) => {
                audioChunksRef.current.push(event.data);
            };
            mediaRecorderRef.current.onstop = () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
                const audioUrl = URL.createObjectURL(audioBlob);
                setAudioURL(audioUrl);
                onAudioRecorded(questionKey, audioBlob);
                // Release the mic so the browser indicator turns off.
                stream.getTracks().forEach((track) => track.stop());
            };
            mediaRecorderRef.current.start();
            setIsRecording(true);
        } catch (err: any) {
            console.error('Error accessing microphone', err);
            const name = err?.name || '';
            const msg =
                name === 'NotAllowedError' || name === 'SecurityError'
                    ? (t('recruitment_audio_permission_denied', {
                          defaultValue:
                              "Accès au micro refusé. Autorisez le micro dans votre navigateur puis réessayez.",
                      }) as string)
                    : name === 'NotFoundError' || name === 'DevicesNotFoundError'
                      ? (t('recruitment_audio_no_mic', {
                            defaultValue:
                                'Aucun micro détecté. Branchez un micro puis réessayez.',
                        }) as string)
                      : (t('recruitment_audio_error_generic', {
                            defaultValue:
                                "Impossible de démarrer l'enregistrement. Réessayez.",
                        }) as string);
            setError(msg);
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
        }
    };

    const handleReplaceRecording = () => {
        // Revoke the previous audio URL to release resources
        if (audioURL) {
            URL.revokeObjectURL(audioURL);
            setAudioURL('');
        }
        
        // Reset recording time and start a new recording
        setRecordingTime(0);
        startRecording(); // Start a new recording session
    };

    const formatTime = (seconds: number) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    return (
        <div className="flex flex-col" style={{ gap: 8 }}>
        <div className="flex items-center space-x-4">
            {/* The recording button only appears if there's no current recording */}
            {!audioURL && !isRecording && (
                <button
                    onClick={startRecording}
                    style={{
                        background: "var(--g-green-deep, #2E8C4D)",
                        color: "white",
                        fontWeight: 700,
                        fontSize: 13,
                        padding: "9px 16px",
                        borderRadius: 8,
                        border: 0,
                        cursor: "pointer",
                    }}
                >
                    {t('recruitment_audio_start_recording')}
                </button>
            )}

            {/* While recording, show the stop button and the recording indicator with the timer */}
            {isRecording && (
                <div className="flex items-center space-x-2">
                    <button
                        onClick={stopRecording}
                        className="bg-red-600 hover:bg-red-800 text-white font-bold py-2 px-4 rounded transition duration-300 animate-pulse"
                    >
                        {t('recruitment_audio_stop_recording')}
                    </button>
                    <div className="flex items-center">
                        <div className="h-4 w-4 bg-red-600 rounded-full animate-ping"></div>
                        <span className="ml-2 text-gray-600">{formatTime(recordingTime)}</span>
                    </div>
                </div>
            )}

            {/* The fieldset with the audio player and replace button only appears after recording has stopped */}
            {audioURL && !isRecording && (
                <fieldset
                    className="w-full rounded-xl"
                    style={{ border: "1px solid var(--g-line, #E6EAE5)", padding: 12 }}
                >
                    <legend
                        style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: "var(--g-burgundy)",
                            padding: "0 6px",
                        }}
                    >
                        {t('recruitment_audio_current_record_title')}
                    </legend>
                    {/* Full-width player on top, then duration + replace below so
                        nothing overflows the card on mobile. */}
                    <audio src={audioURL} controls className="outline-none" style={{ width: "100%" }} />
                    <div
                        className="flex items-center justify-between flex-wrap"
                        style={{ gap: 10, marginTop: 10 }}
                    >
                        <span style={{ fontSize: 12, color: "var(--g-ink-500)" }}>
                            {t('recruitment_audio_duration_label')}: {formatTime(recordingTime)}
                        </span>
                        <button
                            onClick={handleReplaceRecording}
                            style={{
                                fontSize: 12.5,
                                fontWeight: 700,
                                color: "white",
                                background: "var(--g-green-deep, #2E8C4D)",
                                border: 0,
                                borderRadius: 8,
                                padding: "8px 14px",
                                cursor: "pointer",
                            }}
                        >
                            {t('recruitment_audio_replace_recording')}
                        </button>
                    </div>
                </fieldset>
            )}
        </div>
        {error && (
            <p
                role="alert"
                className="text-sm"
                style={{ color: 'var(--g-coral, #C13B5C)', fontWeight: 600 }}
            >
                {error}
            </p>
        )}
        </div>
    );
};

export default AudioRecorder;
